/**
 * Drawing, reconnecting and deleting a transition, as the list PUT to its machine.
 *
 * A transition is declared IN A STATE'S BODY and has no identity of its own
 * (best-decision-008), so every edit is: read the declaring state afresh, change
 * one entry of its list, write the whole list back. These are the rules for
 * which state that is and what the entry says.
 *
 * WHICH STATE: the innermost state that contains both ends. That is where the
 * language resolves `first Idle then Active` from, and it is what the Example
 * does - FlightModes declares the edge between its own two states.
 *
 * WHAT IT SAYS: each end as a name RELATIVE to that state, the way somebody
 * would write it - `Idle`, or `Descent::Landed` for a state one level further
 * in - never a qualified name and never an identifier. Same rule as the
 * `toEdits` below, for the same reason.
 */

import type { Transition, TransitionEdit } from '../../schema'

/**
 * The editable form of what a read gave us.
 *
 * Moved here from the States screen's wholesale editor (issue #50), which the
 * canvas was the last user of. The trap is taking `from`/`to` - a read resolves
 * those to state identifiers, and round-tripping them would rewrite
 * `first Standby then Ascent` as `first ST_STANDBY then ST_ASCENT`: still
 * valid, still resolving, and not what anybody wrote.
 */
export function toEdits(transitions: Transition[]): TransitionEdit[] {
  return transitions.map((edge) => ({
    source: edge.from_reference || edge.from,
    target: edge.to_reference || edge.to,
    trigger: edge.trigger,
    guard: edge.guard,
    effect: edge.effect,
    // As written - but only for an effect that EXISTS. An edge with no effect
    // reads '' because there is nothing to spell, and sending that back would
    // write a newly typed effect as 'do X' instead of the write path's default.
    ...(edge.effect_keyword === undefined || !edge.effect ? {} : { effect_keyword: edge.effect_keyword }),
    // Both names as DECLARED. `name` is a display fallback that answers the
    // short name when there is no other, and sending that back would turn
    // `transition <T_LAUNCH> first A then B` into `transition T_LAUNCH first ...`.
    ...(edge.identifier === undefined
      ? { name: edge.name }
      : { identifier: edge.identifier, name: edge.declared_name ?? '' }),
    // CARRIED, or a save erases it: the server replaces every edge wholesale.
    // The RESOLVED id, unlike the ends above - the server writes a decider's
    // qualified name whatever it is sent, so no id reaches the file, and an id
    // is what its part lookup finds where a scoped reference would not.
    decided_by: edge.decided_by,
  }))
}

/** A state as these rules need it: its identifier and where it sits. */
export interface StateRef {
  id: string
  qualified_name: string
}

export type Verdict<T> = { allowed: true; value: T } | { allowed: false; reason: string }

/**
 * The states containing this one, innermost first.
 *
 * By qualified name, but only through prefixes that ARE states: a package can
 * share a name with a machine, and a prefix match alone could not tell them
 * apart. `states` is every state the model has, not only those on the diagram.
 */
export function enclosingStates(states: StateRef[], qualifiedName: string): StateRef[] {
  const byName = new Map(states.map((state) => [state.qualified_name, state]))
  const segments = qualifiedName.split('::')
  const found: StateRef[] = []
  for (let length = segments.length - 1; length > 0; length -= 1) {
    const state = byName.get(segments.slice(0, length).join('::'))
    if (state) found.push(state)
  }
  return found
}

/** The innermost state containing both ends, or why there is none. */
export function declaringState(states: StateRef[], source: string, target: string): Verdict<StateRef> {
  const around = new Set(enclosingStates(states, target).map((state) => state.qualified_name))
  const common = enclosingStates(states, source).find((state) => around.has(state.qualified_name))
  if (!common) {
    return {
      allowed: false,
      reason: 'A transition is declared inside the state machine both states belong to, and these two share none.',
    }
  }
  return { allowed: true, value: common }
}

/** A state's name as written from inside the state declaring the transition. */
export function relativeName(declaring: StateRef, qualifiedName: string): string {
  return qualifiedName.slice(declaring.qualified_name.length + 2)
}

/** The list with one new transition appended. */
export function withAdded(current: Transition[], declaring: StateRef, source: string, target: string): TransitionEdit[] {
  return [...toEdits(current), { source: relativeName(declaring, source), target: relativeName(declaring, target) }]
}

/**
 * The entry at `index`, checked to still be the edge the diagram drew.
 *
 * The diagram was read earlier than the fresh list. If somebody has changed the
 * state since, the index may now name a different edge, and editing it would
 * change a transition nobody pointed at - so that is refused, not guessed.
 */
function checked(current: Transition[], index: number, drawn: Transition): Verdict<TransitionEdit[]> {
  const now = current[index]
  if (!now || now.from !== drawn.from || now.to !== drawn.to) {
    return { allowed: false, reason: 'That state’s transitions have changed since the diagram was drawn. Reload and try again.' }
  }
  return { allowed: true, value: toEdits(current) }
}

/** The list without the transition at `index`. */
export function withRemoved(current: Transition[], index: number, drawn: Transition): Verdict<TransitionEdit[]> {
  const verdict = checked(current, index, drawn)
  if (!verdict.allowed) return verdict
  return { allowed: true, value: verdict.value.filter((_, at) => at !== index) }
}

/** The list with the transition at `index` changed by `patch`. */
export function withChanged(
  current: Transition[],
  index: number,
  drawn: Transition,
  patch: Partial<TransitionEdit>,
): Verdict<TransitionEdit[]> {
  const verdict = checked(current, index, drawn)
  if (!verdict.allowed) return verdict
  return { allowed: true, value: verdict.value.map((edit, at) => (at === index ? { ...edit, ...patch } : edit)) }
}
